import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, Shield, CheckCircle, ChevronDown, ChevronUp, Calendar, ArrowRight, Heart, Baby, Users, Award, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import LandingPageHeader from '../components/LandingPageHeader';
import { SEOHead, BreadcrumbSchema, FAQSchema, MedicalSpecialtySchema } from '../components/SEOHead';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/t64300ho_SH%20Logo.png";

const GynaecologyPage = () => {
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', treatment: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.treatment) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/specialty-leads`, { ...formData, specialty: 'Gynaecology', source_page: '/gynaecology' });
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'form_submission', form_name: 'gynaecology_lead', form_department: 'Gynaecology', form_source: '/gynaecology' });
      }
      if (window.gtag) { window.gtag('event', 'conversion', { send_to: 'AW-17542370694/gynaecology_lead' }); }
      toast.success('Thank you! Our team will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', treatment: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const treatments = [
    'General Gynecology', 'Pregnancy Care', 'High-Risk Pregnancy', 'Fertility & IVF',
    'Laparoscopic Surgery', 'PCOS Treatment', 'Menopause Care', 'Other',
  ];

  const services = [
    { title: 'Normal & High-Risk Pregnancy', desc: 'Antenatal check-ups, scans, high-risk pregnancy protocols & safe deliveries', icon: Baby, color: '#c41e3a' },
    { title: 'Infertility (IUI / IVF)', desc: 'Advanced fertility treatments including IUI, IVF & tubal re-canalization for couples', icon: Users, color: '#7b1fa2' },
    { title: 'CAPSI Cancer Screening', desc: 'Cancer Awareness, Prevention & Screening Initiative — Colposcopy & Pap Smear', icon: Shield, color: '#0066b3' },
    { title: 'Laparoscopic Gynaecology', desc: 'Minimally invasive surgery for fibroids, cysts, endometriosis & faster recovery', icon: Shield, color: '#00897b' },
    { title: 'Aesthetic Gynaecology', desc: 'Advanced aesthetic & reconstructive procedures for women\'s wellness', icon: Heart, color: '#e91e8c' },
    { title: 'Menopause & PCOS Care', desc: 'Hormone balance, PCOS management, bone health & holistic women\'s care', icon: Heart, color: '#d4a24c' },
  ];

  const stats = [
    { value: '75,000+', label: 'Couples Treated' },
    { value: '40,000+', label: 'Safe Deliveries' },
    { value: '40+', label: 'Years Legacy' },
    { value: 'NABH', label: 'Accredited' },
  ];

  const trustPoints = [
    'Lifetime Achievement Award 2013', 'FOGSI & OGSSI Member', '24/7 NICU & ICU',
    'CAPSI Cancer Screening', 'Insurance & TPA Cashless', 'NABH Accredited',
  ];

  const doctors = [
    {
      name: 'Dr. Sivakamu Dhandapani',
      qualifications: 'M.D., D.G.O., MRCOG (London, UK)',
      experience: 'Chairperson | 40+ Years Experience',
      specialization: 'Obstetrics, Gynaecology, Infertility, Colposcopy & Endoscopy',
      memberships: 'FOGSI | IFUMB | OGSSI | IMA | Lifetime Achievement Award 2013',
      image: 'https://sakthihospitals.com/wp-content/uploads/2023/06/sakthi-website_home-pg03.jpg',
    },
    {
      name: 'Dr. Usha Rani Minna',
      qualifications: 'MBBS, DGO, MRCOG (UK)',
      experience: 'Senior Consultant',
      specialization: 'Obstetrics, Gynecology, Endoscopy & Fertility',
      memberships: '',
      image: 'https://sakthihospitals.com/wp-content/uploads/2023/06/welcome-sec.jpg',
    },
  ];

  const testimonials = [
    { name: 'Priya R.', location: 'Triplicane', rating: 5, text: 'Had my delivery at Sakthi Hospital. The doctors and staff were incredibly supportive throughout my pregnancy. My baby and I received the best care possible.' },
    { name: 'Kavitha S.', location: 'Royapettah', rating: 5, text: 'After 5 years of trying, the fertility treatment at Sakthi Hospital helped us conceive. Dr. Sivakamu madam is truly a blessing. We are forever grateful.' },
    { name: 'Meena K.', location: 'Mylapore', rating: 5, text: 'PCOS treatment at Sakthi Hospital changed my life. The doctors were patient and the treatment plan was perfect. Highly recommend for all women\'s health issues.' },
  ];

  const faqs = [
    { q: 'How do I choose the best gynecologist near me?', a: 'Look for experienced doctors with proper qualifications (MD/DGO), hospital accreditation (NABH), patient reviews, and comprehensive services. Sakthi Hospital offers 40+ years of expertise with board-certified specialists.' },
    { q: 'Do you handle high-risk pregnancies?', a: 'Yes, we have specialized protocols for high-risk pregnancies including gestational diabetes, preeclampsia, and multiple pregnancies. Our 24/7 ICU and NICU support ensure safety for both mother and baby.' },
    { q: 'Is fertility treatment at Sakthi Hospital affordable?', a: 'We offer competitive and transparent pricing for all fertility treatments. Our team can help you understand costs and explore insurance options. Contact us for a detailed consultation.' },
    { q: 'Do you provide minimally invasive surgeries?', a: 'Yes, we offer advanced laparoscopic and endoscopic procedures for conditions like fibroids, ovarian cysts, and endometriosis, ensuring faster recovery and minimal scarring.' },
    { q: 'Can I book an appointment online?', a: 'Yes! You can fill the form on this page or call us at +91 98402 64444. Our team will schedule your appointment with the right specialist.' },
  ];

  const LeadForm = ({ className = '' }) => (
    <form onSubmit={handleSubmit} className={className}>
      <input type="hidden" id="zc_gad" name="zc_gad" value="" />
      <div className="space-y-3">
        <div>
          <Label className="text-sm font-medium text-gray-700">Your Name</Label>
          <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Full Name" className="mt-1 h-11" data-testid="gynae-form-name" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
          <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91 98402 64444" className="mt-1 h-11" data-testid="gynae-form-phone" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Email Address</Label>
          <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="mt-1 h-11" data-testid="gynae-form-email" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Select Treatment</Label>
          <Select value={formData.treatment} onValueChange={(v) => setFormData({...formData, treatment: v})}>
            <SelectTrigger className="mt-1 h-11" data-testid="gynae-form-treatment"><SelectValue placeholder="Select Treatment" /></SelectTrigger>
            <SelectContent>{treatments.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 bg-[#e91e8c] hover:bg-[#c4177a] text-white font-semibold rounded-lg" data-testid="gynae-form-submit">
          {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Submitting...</> : <><Calendar size={18} className="mr-2" /> Book Appointment Now</>}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="gynaecology-page">
      <SEOHead
        title="Best Gynaecology & Maternity Hospital in Triplicane Chennai | Sakthi Hospital"
        description="Safe pregnancy & joyful delivery at Sakthi Hospital Chennai. 40+ years of expert gynaecology care. Normal delivery, IVF, PCOS treatment, laparoscopic surgery. Dr. Sivakamu Dhandapani - MRCOG London. 75,000+ couples treated. NABH Accredited. Book free consultation."
        path="/gynaecology"
        keywords="gynaecology hospital Chennai, maternity hospital Triplicane, best gynecologist Chennai, safe delivery hospital, IVF treatment Chennai, PCOS treatment, pregnancy care Chennai, Dr Sivakamu, normal delivery hospital near me, NABH maternity hospital"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Gynaecology & Maternity', path: '/gynaecology' }]} />
      <FAQSchema questions={faqs} />
      <MedicalSpecialtySchema
        name="Gynaecology & Obstetrics"
        description="Comprehensive women's health care including pregnancy, fertility treatment, PCOS management, and minimally invasive gynaecological surgery at Sakthi Hospital Chennai."
        procedures={['Normal Delivery', 'C-Section', 'IVF & IUI', 'Laparoscopic Gynaecology', 'PCOS Treatment', 'CAPSI Cancer Screening', 'High-Risk Pregnancy', 'Aesthetic Gynaecology']}
        doctor={{ name: 'Dr. Sivakamu Dhandapani', qualifications: 'M.D., D.G.O., MRCOG (London, UK) - 40+ Years Experience' }}
      />
      {/* Header */}
      <LandingPageHeader accentColor="#e91e8c" subtitle="Gynaecology & Maternity" />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#fff0f6] via-white to-[#fce4f0] overflow-hidden" data-testid="gynae-hero">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#e91e8c]/5 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#e91e8c]/10 text-[#e91e8c] rounded-full text-xs font-semibold">NABH Accredited</span>
                <span className="px-3 py-1 bg-[#e91e8c]/10 text-[#e91e8c] rounded-full text-xs font-semibold">5-Star Rated Care</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0f172a] leading-[1.1] tracking-tight" data-testid="gynae-headline">
                Safe Pregnancy &{' '}
                <span className="text-[#e91e8c]">Joyful Delivery</span>
                <span className="block text-lg sm:text-xl font-semibold text-[#64748b] mt-3">At Sakthi Hospital, Triplicane</span>
              </h1>
              <p className="text-base text-[#5a6b7c] leading-relaxed max-w-lg">
                Thousands of Chennai Moms Trust Us For Compassionate, Expert-Led Deliveries. From PCOS to Parenthood — Everything Women Need, in One Trusted Hospital.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919840264444" className="bg-[#e91e8c] text-white px-7 py-3.5 rounded-lg font-semibold shadow-lg shadow-[#e91e8c]/25 hover:bg-[#c4177a] transition-all flex items-center gap-2" data-testid="gynae-call-btn">
                  <Phone size={18} /> Call Now
                </a>
                <a href="#gynae-booking" className="bg-white text-[#e91e8c] border-2 border-[#e91e8c] px-7 py-3.5 rounded-lg font-semibold hover:bg-[#e91e8c] hover:text-white transition-all flex items-center gap-2" data-testid="gynae-book-btn">
                  <Calendar size={18} /> Free Consultation
                </a>
              </div>
            </div>
            {/* Right - Lead Form */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-pink-100">
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-[#0f172a]">Check Your Treatment Cost</h3>
                <p className="text-sm text-[#64748b]">Get a free consultation & cost estimate</p>
              </div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="bg-[#e91e8c] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            {stats.map((s) => (
              <div key={s.value}>
                <div className="text-3xl font-extrabold">{s.value}</div>
                <div className="text-sm opacity-80 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">Complete Women's Health Under One Roof</h2>
            <p className="text-[#64748b] max-w-xl mx-auto">From PCOS to Parenthood — Everything Women Need, in One Trusted Hospital</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.title} className="bg-gradient-to-br from-[#fff0f6] to-white rounded-2xl p-6 border border-pink-100 hover:shadow-lg transition-all" data-testid={`gynae-service-${s.title}`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${s.color}15`, color: s.color }}>
                  <s.icon size={28} />
                </div>
                <h3 className="text-lg font-bold text-[#0f172a] mb-2">{s.title}</h3>
                <p className="text-sm text-[#5a6b7c] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="py-16 bg-gradient-to-b from-[#fff0f6] to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">Why 1,50,000+ Families Trust Sakthi Hospital</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trustPoints.map((point) => (
              <div key={point} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-pink-50">
                <CheckCircle size={22} className="text-[#e91e8c] flex-shrink-0" />
                <span className="font-medium text-[#0f172a]">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctors */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Our Expert Doctors</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {doctors.map((doc, idx) => (
              <div key={doc.name} className="bg-gradient-to-br from-[#fff0f6] to-white rounded-2xl p-6 border border-pink-100 text-center" data-testid={`gynae-doctor-${idx}`}>
                <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4 border-4 border-[#e91e8c]/20">
                  <img src={doc.image} alt={doc.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-lg font-bold text-[#0f172a]">{doc.name}</h3>
                <p className="text-[#e91e8c] font-semibold text-sm mt-1">{doc.experience}</p>
                <p className="text-sm text-[#5a6b7c] mt-1">{doc.qualifications}</p>
                <p className="text-xs text-[#94a3b8] mt-1">{doc.specialization}</p>
                {doc.memberships && <p className="text-xs text-[#e91e8c]/80 mt-2 font-medium">{doc.memberships}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gradient-to-b from-[#fff0f6] to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">What Patients Say About Us</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-6 shadow-md border border-pink-50" data-testid={`gynae-testimonial-${t.name}`}>
                <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={`star-${j}`} size={16} fill="#d4a24c" stroke="#d4a24c" />)}</div>
                <p className="text-sm text-[#3a4a5c] leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 bg-[#e91e8c] rounded-full flex items-center justify-center text-white font-bold text-sm">{t.name[0]}</div>
                  <div>
                    <div className="font-semibold text-sm text-[#0f172a]">{t.name}</div>
                    <div className="text-xs text-[#94a3b8]">{t.location}, Chennai</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-10">FAQs - Gynaecology & Women's Health</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={faq.q} className="border border-pink-100 rounded-xl overflow-hidden" data-testid={`gynae-faq-${idx}`}>
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#fff0f6] transition-colors">
                  <span className="font-semibold text-[#0f172a] pr-4">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp size={20} className="text-[#e91e8c] flex-shrink-0" /> : <ChevronDown size={20} className="text-[#94a3b8] flex-shrink-0" />}
                </button>
                {openFaq === idx && <div className="px-5 pb-5 text-sm text-[#5a6b7c] leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-gradient-to-b from-[#fff0f6] to-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-8">Our Location</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden h-72 shadow-lg">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.0!2d80.2757397!3d13.0570045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526605d0a030d5%3A0x6e079bd99529d311!2sSakthi%20Hospital!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Sakthi Hospital Location"></iframe>
            </div>
            <div className="flex flex-col justify-center space-y-4">
              <div className="flex items-start gap-3"><MapPin size={20} className="text-[#e91e8c] mt-1 flex-shrink-0" /><div><p className="font-semibold text-[#0f172a]">Sakthi Hospital & Research Centre</p><p className="text-sm text-[#5a6b7c]">175, Big Street, Triplicane, Chennai - 600005</p></div></div>
              <div className="flex items-center gap-3"><Phone size={20} className="text-[#e91e8c] flex-shrink-0" /><div><p className="text-sm text-[#5a6b7c]">+91 44 2844 2626 | +91 98402 64444</p></div></div>
              <div className="flex items-center gap-3"><Clock size={20} className="text-[#e91e8c] flex-shrink-0" /><div><p className="text-sm text-[#5a6b7c]">OPD: 9:00 AM - 9:00 PM | Emergency: 24/7</p></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section id="gynae-booking" className="py-16 bg-[#e91e8c]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="text-white space-y-4">
              <h2 className="text-3xl font-bold">Ready for Expert Women's Health & Pregnancy Care?</h2>
              <p className="opacity-90">Book your FREE consultation today and take the first step toward better health.</p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919840264444" className="bg-white text-[#e91e8c] px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-50 transition-colors">
                  <Phone size={18} /> Call Now
                </a>
                <a href="https://wa.me/919840264444" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-[#20bd5a] transition-colors">
                  WhatsApp Now
                </a>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <h3 className="text-lg font-bold text-center text-[#0f172a] mb-4">Book Your Consultation Today</h3>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer link back */}
      <div className="bg-[#0f172a] text-white py-6 text-center">
        <Link to="/" className="text-sm text-white/70 hover:text-[#e91e8c] transition-colors flex items-center gap-2 justify-center">
          <ArrowRight size={14} className="rotate-180" /> Back to Sakthi Hospital Home
        </Link>
        <p className="text-xs text-white/50 mt-2">© 2025 Sakthi Hospital & Research Centre. All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default GynaecologyPage;
